import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useAutosave
 *
 * Debounced background save. Designed for post editors.
 *
 * Rules:
 *  - Does nothing while `enabled === false` (e.g. the entry has no id yet, so
 *    a new empty entry never spawns rows).
 *  - Waits `delayMs` after the last change (via `dirty` transitioning to true
 *    and value stabilising) before calling `save`.
 *  - Skips if not `dirty`, or if a save is already in flight, or if the last
 *    fingerprint we saved matches the current value.
 *  - `save` should return the saved payload (or null on error). It is called
 *    without a status override — meaning drafts stay drafts and published
 *    entries stay published.
 *  - Exposes { status, lastSavedAt, saveNow }.
 *
 * @param {object} params
 * @param {boolean} params.enabled   Turn autosave on/off (e.g. false when
 *                                    editing an unsaved new entry).
 * @param {boolean} params.dirty     Whether the form has unsaved changes.
 * @param {any}    params.value      A serialisable snapshot of the form.
 *                                    Used to fingerprint and skip identical saves.
 * @param {(overrideStatus?: 'draft'|'published') => Promise<any>} params.save
 * @param {number} [params.delayMs=2000]
 */
export function useAutosave({ enabled, dirty, value, save, delayMs = 2000 }) {
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const lastFingerprintRef = useRef(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const doSave = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setStatus('saving');
    try {
      const result = await saveRef.current?.();
      if (result) {
        setStatus('saved');
        setLastSavedAt(new Date());
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (!dirty) return;
    let fp;
    try {
      fp = JSON.stringify(value);
    } catch {
      fp = String(Date.now());
    }
    if (fp === lastFingerprintRef.current) return;
    // schedule a save after `delayMs` of stability
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      lastFingerprintRef.current = fp;
      doSave();
    }, delayMs);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [enabled, dirty, value, delayMs, doSave]);

  // reset "saved" indicator after 3s so it fades to idle
  useEffect(() => {
    if (status !== 'saved') return;
    const t = window.setTimeout(() => setStatus('idle'), 3500);
    return () => window.clearTimeout(t);
  }, [status]);

  return { status, lastSavedAt, saveNow: doSave };
}

/** Compact human label for the autosave indicator */
export function autosaveLabel(status, lastSavedAt) {
  if (status === 'saving') return 'Saving\u2026';
  if (status === 'error') return 'Autosave failed';
  if (status === 'saved') return 'Saved just now';
  if (lastSavedAt instanceof Date) {
    try {
      return `Saved at ${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch {
      /* noop */
    }
  }
  return null;
}
